import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { isSupabaseConfigured, supabase } from '../lib/supabase'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(isSupabaseConfigured)

  const resolveAdmin = useCallback(async (nextSession) => {
    if (!supabase || !nextSession?.user) {
      setIsAdmin(false)
      return false
    }

    const { data, error } = await supabase.rpc('is_admin')
    if (error) {
      console.error('Admin authorization check failed:', error)
      setIsAdmin(false)
      return false
    }

    setIsAdmin(Boolean(data))
    return Boolean(data)
  }, [])

  useEffect(() => {
    if (!supabase) {
      setLoading(false)
      return undefined
    }

    let isMounted = true

    supabase.auth.getSession().then(async ({ data }) => {
      if (!isMounted) return
      setSession(data.session)
      await resolveAdmin(data.session)
      if (isMounted) setLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      setSession(nextSession)
      await resolveAdmin(nextSession)
      setLoading(false)
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [resolveAdmin])

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      isAdmin,
      loading,
      configured: isSupabaseConfigured,
      signIn: async (email, password) => {
        if (!supabase) throw new Error('Supabase is not configured.')
        const result = await supabase.auth.signInWithPassword({ email, password })
        const admin = result.data.session ? await resolveAdmin(result.data.session) : false
        return { ...result, isAdmin: admin }
      },
      signOut: async () => {
        if (!supabase) return
        await supabase.auth.signOut()
        setIsAdmin(false)
      },
    }),
    [isAdmin, loading, resolveAdmin, session],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
